import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle, X } from "lucide-react";
import { API } from "../lib/api";

export default function BookingSuccessPage() {
  const [status, setStatus] = useState("checking");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentInfo(response.data);
      
      if (response.data.payment_status === 'paid') {
        setStatus("success");
      } else if (response.data.status === 'expired') {
        setStatus("expired");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Error checking payment:", error);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6 py-12" data-testid="booking-success-page">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 shadow-sm border border-stone-100 text-center">
        {status === "checking" && (
          <>
            <Loader2 size={48} className="text-teal-600 mx-auto animate-spin" />
            <h1 className="text-2xl font-serif text-stone-900 mt-6">Processing Payment</h1>
            <p className="text-stone-500 mt-2">Please wait while we confirm your booking...</p>
          </>
        )}
        
        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle size={32} className="text-emerald-600" />
            </div>
            <h1 className="text-2xl font-serif text-stone-900 mt-6">Booking Confirmed!</h1>
            <p className="text-stone-500 mt-2">Thank you for your booking. We've sent a confirmation to your email.</p>
            {paymentInfo && (
              <div className="mt-6 p-4 bg-stone-50 rounded-xl text-left">
                <p className="text-sm text-stone-500">Amount paid</p>
                <p className="text-2xl font-bold text-stone-900">
                  €{(paymentInfo.amount_total / 100).toFixed(2)}
                </p>
              </div>
            )}
            <button
              onClick={() => navigate("/")}
              className="mt-6 w-full bg-teal-700 hover:bg-teal-800 text-white py-3 rounded-xl font-medium transition-colors"
            >
              Return to Home
            </button>
          </>
        )}
        
        {(status === "error" || status === "expired" || status === "timeout") && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <X size={32} className="text-red-600" />
            </div>
            <h1 className="text-2xl font-serif text-stone-900 mt-6">
              {status === "timeout" ? "Payment Pending" : "Payment Issue"}
            </h1>
            <p className="text-stone-500 mt-2">
              {status === "timeout" 
                ? "Your payment is still being processed. Check your email for confirmation."
                : "There was an issue with your payment. Please try again or contact support."}
            </p>
            <button
              onClick={() => navigate("/upsells")}
              className="mt-6 w-full bg-teal-700 hover:bg-teal-800 text-white py-3 rounded-xl font-medium transition-colors"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
